import { Flex, Grid, GridItem, Text, useDisclosure } from '@chakra-ui/react';
import ChartMapImage from 'assets/images/chart_map.png';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import { CHART_CREATED, CLICK_ON_PAID_FEATURE, TABLE_CREATED, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { SIDE_MENU_WIDGET_TYPES } from 'modules/Editor/components/SideMenuSettings/SideMenuSettings.config';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { addNewWidget, getInitialWidgetPosition, useAppDispatch } from 'modules/Editor/store';
import { setWidgetSettingsView } from 'modules/Editor/store/editorSettingsSlice';
import { PermissionBadge, UserType, UserTypes, hasResourcePermission, useUser } from 'modules/User';
import { ReactElement, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { getChartInfo } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { CHART_MENU_DATA, CHART_TYPE_TO_DEFAULT_DATA_MAP, ChartType } from './ChartsWidgetMenu.config';

export const ChartsWidgetMenu = (): ReactElement => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.chartsMenu.categories',
  });

  const createWidgetData = (chartType: ChartType) => {
    const widgetData = CHART_TYPE_TO_DEFAULT_DATA_MAP[chartType];
    const { top, left } = getInitialWidgetPosition(true);

    widgetData.widgetData = { ...widgetData.widgetData, topPx: top, leftPx: left };
    return widgetData;
  };

  const doAddWidget = (chartType: ChartType) => {
    const widgetData = createWidgetData(chartType);

    dispatch(addNewWidget(widgetData));

    // Open side menu when chart is added
    if (SIDE_MENU_WIDGET_TYPES.includes(widgetData.widgetType)) {
      dispatch(setWidgetSettingsView(true));
    }

    if (chartType === ChartType.Table) {
      Mixpanel.track(TABLE_CREATED.eventName, { from: TABLE_CREATED.from.LeftPanel });
    } else {
      const { widgetTypeName } = getChartInfo(widgetData.widgetType, widgetData.widgetData);

      Mixpanel.track(CHART_CREATED.eventName, {
        from: CHART_CREATED.from.LeftPanel,
        preset: chartType,
        chart_type: widgetTypeName,
      });
    }
  };

  return (
    <Flex p={4} direction={'column'} gap={4} data-testid={'widgetmenu-chart-panel'} h={'full'} overflow={'auto'}>
      {CHART_MENU_DATA.map((chartCategory) => {
        return (
          <ChartCategory
            key={chartCategory.category}
            title={t(chartCategory.category as keyof typeof t)}
            widgets={chartCategory.chartWidgets}
            onClickWidget={doAddWidget}
            createWidgetData={createWidgetData}
          />
        );
      })}
    </Flex>
  );
};

interface ChartCategoryProps {
  title: string;
  widgets: {
    type: ChartType;
    icon: string;
    name: string;
    permission?: UserType;
  }[];
  onClickWidget: (chartType: ChartType) => void;
  createWidgetData: (chartType: ChartType) => NewWidget;
}

export const ChartCategory = ({ title, widgets, onClickWidget, createWidgetData }: ChartCategoryProps) => {
  const { userType } = useUser();
  const { t } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.chartsMenu.chartWidgets',
  });
  const { trackChameleon } = useChameleon();
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();
  const upgradeModalRef = useRef<{ name: string; plan: UserType }>({ name: '', plan: UserTypes.Free });

  const onGated = useCallback(
    (widgetName: string, plan: UserType) => {
      Mixpanel.track(UPGRADE.eventName, {
        Feature: UPGRADE.feature.PremiumChart,
        chart_type: widgetName,
        'User Plan': getUserPlanProperty(userType),
      });
      trackChameleon(UPGRADE.eventName);

      setAnalyticsUpgradeFeatureProp(UPGRADE.feature.PremiumChart, widgetName);
      window.open(`${UPGRADE_REDIRECT_LINK}/${plan}`, '_self');
    },
    [userType, trackChameleon],
  );

  const onClickUpgrade = () => onGated(upgradeModalRef.current.name, upgradeModalRef.current.plan);

  return (
    <Flex direction={'column'} role={'group'}>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={upgradeModalRef.current.plan}
        modalImg={ChartMapImage}
        modalType={ModalUpgradeTypeEnum.MapCharts}
      />
      <Text mb={4} color={'font.500'} fontWeight={'500'} fontSize={'sm'}>
        {title}
      </Text>
      <Grid templateColumns={'repeat(2, 1fr)'} columnGap={3} rowGap={3}>
        {widgets.map((widget) => {
          const gatedPermission = hasResourcePermission(userType, widget.permission ?? UserTypes.Free)
            ? undefined
            : widget.permission;
          const onClick = gatedPermission
            ? () => {
                upgradeModalRef.current = { name: widget.name, plan: widget.permission! };
                onOpenUpgradeModal();
                Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
                  Feature: UPGRADE.feature.PremiumChart,
                  chart_type: widget.name,
                  'User Plan': getUserPlanProperty(userType),
                });
              }
            : onClickWidget;

          return (
            <GridItem key={widget.name}>
              <ChartThumbnail
                title={t(widget.type as keyof typeof t)}
                type={widget.type}
                icon={widget.icon}
                gated={gatedPermission}
                onClick={onClick}
                createWidgetData={createWidgetData}
              />
            </GridItem>
          );
        })}
      </Grid>
    </Flex>
  );
};

interface ChartThumbnailProps {
  title: string;
  type: ChartType;
  icon: string;
  onClick: (chartType: ChartType) => void;
  createWidgetData: (chartType: ChartType) => NewWidget;
  gated?: UserType;
}
const ChartThumbnail = ({ title, type, icon, gated, onClick, createWidgetData }: ChartThumbnailProps) => {
  const handleClick = useCallback(() => {
    onClick(type);
  }, [onClick, type]);

  return (
    <WidgetDragDropWrapper
      widgetData={createWidgetData(type)}
      type={type}
      isDraggable={!gated}
      dragImage={
        <Flex
          align='center'
          direction='column'
          p={4}
          alignItems={'center'}
          justifyContent={'center'}
          border={'1px solid'}
          borderColor={'divider.gray'}
          borderRadius={'base'}
          w='152px'
          h='97px'
        >
          <img src={icon} alt='' />
          <Text color={'font.500'} fontSize={'xs'}>
            {title}
          </Text>
        </Flex>
      }
      dragImageOffset={{ x: 76, y: 48.5 }}
      wrapperContentProps={{ overflow: 'visible' }}
      trackingEventData={{
        event_name: CHART_CREATED.eventName,
        properties: {
          from: type === ChartType.Table ? TABLE_CREATED.from.LeftPanel : CHART_CREATED.from.LeftPanel,
          preset: type,
          chart_type: getChartInfo(createWidgetData(type).widgetType, createWidgetData(type).widgetData).widgetTypeName,
        },
      }}
    >
      <Flex
        w={'full'}
        h={'full'}
        direction={'column'}
        gap={1}
        p={4}
        alignItems={'center'}
        justifyContent={'center'}
        as={'button'}
        border={'1px solid'}
        borderColor={'divider.gray'}
        borderRadius={'base'}
        onClick={handleClick}
        transitionDuration={'200ms'}
        position={'relative'}
        _hover={{
          bg: 'hover.gray',
        }}
        data-testid={`chartsmenu-${type.toLowerCase()}-btn`}
      >
        {!!gated && <PermissionBadge permission={gated} position='absolute' top={-2} right={2} />}
        <Flex align='center' h={41}>
          {/* need to set pointer events to none here as drag event is triggered for image / svg elements within the draggable container */}
          <img src={icon} alt='' role='presentation' style={{ pointerEvents: 'none' }} />
        </Flex>
        <Text color={'font.500'} fontSize={'xs'}>
          {title}
        </Text>
      </Flex>
    </WidgetDragDropWrapper>
  );
};
